import React, { FC, ReactNode, useRef } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";

import { Header } from "./Header";
import { IntercomObserver } from "../../components/observers";
import { useValidateInvoiceAmountWithFirstKYCLevel } from "../../hooks/useValidateInvoiceAmountWithFirstKYCLevel";
import { selectCurrentUser } from "../../store/reducers/user";
import { selectInvoice, selectStep } from "../../store/reducers/invoice";
import { InvoiceStatus } from "../../types/InvoiceTypes";

type Props = {
  children?: ReactNode;
  action?: ReactNode;
};

export const SteppedInvoiceLayout: FC<Props> = ({ children, action }) => {
  const actionContainerRef = useRef<HTMLDivElement | null>(null);
  const invoice = useSelector(selectInvoice);
  const currentUser = useSelector(selectCurrentUser);
  const step = useSelector(selectStep);
  const { valid: validAmount } =
    useValidateInvoiceAmountWithFirstKYCLevel(invoice);

  if (!invoice) {
    return <></>;
  }

  const { kyc_complete_and_approved, requires_first_level_kyc } = currentUser;
  const { status } = invoice;

  const contentClassNames = (() => {
    if (status === InvoiceStatus.DRAFT) {
      switch (step) {
        case 2:
          return validAmount ? "mb-[88px] pb-[40px]" : "mb-[257px] pb-[64px]";

        case 4:
          return kyc_complete_and_approved || !requires_first_level_kyc
            ? "mb-[177px] pb-[64px]" // Margin bottom in the last step when KYC is complete
            : "mb-[257px] pb-[64px]"; // Margin bottom in the last step when KYC is not complete

        default:
          return "mb-[88px] pb-[40px]";
      }
    } else {
      return "mb-[88px] pb-[40px]";
    }
  })();

  return (
    <div className="w-full flex flex-row bg-purple-50">
      <div className="w-screen flex flex-col mx-auto h-screen">
        <Header />

        <div
          className={classNames(
            "mt-[104px] overflow-y-auto",
            contentClassNames
          )}
        >
          <div className="pt-0 p-4">{children}</div>
        </div>

        <div
          className="fixed bottom-0 flex flex-col items-center w-full p-4 !z-[25] bg-purple-50"
          ref={actionContainerRef}
        >
          {action}
        </div>
      </div>

      <IntercomObserver footer={actionContainerRef} />
    </div>
  );
};
